// OWNER: platform module (notifications area). TanStack Query hooks for notifications, preferences,
// This week and deadline actions (FR-11.1, FR-11.2). `useThisWeek` is also used by the Home page.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  THIS_WEEK_DEFAULT_DAYS,
  type DeadlineAssigneeList,
  type NotificationDto,
  type NotificationList,
  type NotificationMarkAllReadResponse,
  type NotificationPreferencesDto,
  type NotificationUnreadCount,
  type ThisWeekItem,
  type ThisWeekResponse,
  type UpdateNotificationPreferencesRequest,
} from '@seventide/types';
import { useWorkspace } from '../../../hooks/useWorkspace';
import { api, wsPath } from '../../../lib/api';
import { qk } from '../../../lib/queryClient';

const P = 'platform';
export const notificationKeys = {
  all: (wsId: string) => qk.ws(wsId, P, 'notifications'),
  unread: (wsId: string) => qk.ws(wsId, P, 'notifications', 'unread-count'),
  list: (wsId: string, filter: 'unread' | 'all', offset: number) =>
    qk.ws(wsId, P, 'notifications', 'list', { filter, offset }),
  prefs: (wsId: string) => qk.ws(wsId, P, 'notification-preferences'),
  thisWeek: (wsId: string, days?: number) =>
    days === undefined ? qk.ws(wsId, P, 'this-week') : qk.ws(wsId, P, 'this-week', { days }),
  assignees: (wsId: string, deadlineId: string) => qk.ws(wsId, P, 'deadline-assignees', deadlineId),
};

// ---------------------------------------------------------------- activity (stop polling when idle)

/** Don't keep polling for someone who has walked away: lets the server's idle sign-out happen. */
export const POLL_IDLE_AFTER_MS = 20 * 60_000;

/** True while the person has interacted with the page in the last `idleAfterMs`. */
export function useRecentlyActive(idleAfterMs = POLL_IDLE_AFTER_MS): boolean {
  const [active, setActive] = useState(true);
  useEffect(() => {
    let last = Date.now();
    const mark = () => {
      last = Date.now();
      setActive(true);
    };
    const events = ['pointerdown', 'keydown', 'scroll', 'focus'] as const;
    for (const ev of events) window.addEventListener(ev, mark, { passive: true });
    const timer = window.setInterval(() => setActive(Date.now() - last < idleAfterMs), 60_000);
    return () => {
      for (const ev of events) window.removeEventListener(ev, mark);
      window.clearInterval(timer);
    };
  }, [idleAfterMs]);
  return active;
}

// ---------------------------------------------------------------- notifications

/** Unread count for the header bell: every 60 s while visible and active, and on window focus. */
export function useUnreadCount(wsId: string) {
  const active = useRecentlyActive();
  return useQuery({
    queryKey: notificationKeys.unread(wsId),
    queryFn: ({ signal }) =>
      api.get<NotificationUnreadCount>(wsPath(wsId, P, 'notifications', 'unread-count'), { signal }),
    refetchInterval: active ? 60_000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    enabled: Boolean(wsId),
  });
}

export const NOTIFICATIONS_PAGE_SIZE = 20;

export function useNotifications(filter: 'unread' | 'all', offset: number) {
  const { wsId } = useWorkspace();
  return useQuery({
    queryKey: notificationKeys.list(wsId, filter, offset),
    queryFn: ({ signal }) =>
      api.get<NotificationList>(wsPath(wsId, P, 'notifications'), {
        query: { unread: filter === 'unread' ? 'true' : undefined, limit: NOTIFICATIONS_PAGE_SIZE, offset },
        signal,
      }),
    placeholderData: (prev) => prev,
  });
}

export function useMarkNotificationRead() {
  const { wsId } = useWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<NotificationDto>(wsPath(wsId, P, 'notifications', id, 'read')),
    onSettled: () => qc.invalidateQueries({ queryKey: notificationKeys.all(wsId) }),
  });
}

export function useMarkAllNotificationsRead() {
  const { wsId } = useWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<NotificationMarkAllReadResponse>(wsPath(wsId, P, 'notifications', 'read-all')),
    onSettled: () => qc.invalidateQueries({ queryKey: notificationKeys.all(wsId) }),
  });
}

export function useNotificationPreferences() {
  const { wsId } = useWorkspace();
  return useQuery({
    queryKey: notificationKeys.prefs(wsId),
    queryFn: ({ signal }) =>
      api.get<NotificationPreferencesDto>(wsPath(wsId, P, 'notification-preferences'), { signal }),
  });
}

export function useSaveNotificationPreferences() {
  const { wsId } = useWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateNotificationPreferencesRequest) =>
      api.put<NotificationPreferencesDto>(wsPath(wsId, P, 'notification-preferences'), body),
    onSuccess: (data) => qc.setQueryData(notificationKeys.prefs(wsId), data),
  });
}

// ---------------------------------------------------------------- This week (FR-11.2)

/** Deadlines due in the next `days` days plus overdue, in priority order. Used by This week and Home. */
export function useThisWeek(days: number = THIS_WEEK_DEFAULT_DAYS) {
  const { wsId } = useWorkspace();
  return useQuery({
    queryKey: notificationKeys.thisWeek(wsId, days),
    queryFn: ({ signal }) =>
      api.get<ThisWeekResponse>(wsPath(wsId, P, 'this-week'), { query: { days }, signal }),
  });
}

export type DeadlineAction =
  { type: 'complete' } | { type: 'reopen' } | { type: 'dismiss' } | { type: 'snooze'; until: string };

/**
 * Complete / reopen / dismiss / snooze. Complete, dismiss and snooze remove the item from every cached
 * This week list straight away (optimistic) and roll back if the server refuses.
 */
export function useDeadlineAction() {
  const { wsId } = useWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: DeadlineAction }) =>
      api.post<ThisWeekItem>(
        wsPath(wsId, P, 'deadlines', id, action.type),
        action.type === 'snooze' ? { until: action.until } : undefined,
      ),
    onMutate: async ({ id, action }) => {
      if (action.type === 'reopen')
        return { snapshots: [] as [readonly unknown[], ThisWeekResponse | undefined][] };
      await qc.cancelQueries({ queryKey: notificationKeys.thisWeek(wsId) });
      const snapshots = qc.getQueriesData<ThisWeekResponse>({ queryKey: notificationKeys.thisWeek(wsId) });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        const removed = data.items.find((i) => i.id === id);
        if (!removed) continue;
        qc.setQueryData<ThisWeekResponse>(key, {
          ...data,
          items: data.items.filter((i) => i.id !== id),
          overdueCount: data.overdueCount - (removed.overdue ? 1 : 0),
          dueSoonCount: data.dueSoonCount - (removed.overdue ? 0 : 1),
        });
      }
      return { snapshots };
    },
    onError: (_e, _v, context) => {
      for (const [key, data] of context?.snapshots ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.thisWeek(wsId) });
      void qc.invalidateQueries({ queryKey: notificationKeys.all(wsId) });
    },
  });
}

export function useDeadlineAssignees(deadlineId: string, enabled: boolean) {
  const { wsId } = useWorkspace();
  return useQuery({
    queryKey: notificationKeys.assignees(wsId, deadlineId),
    queryFn: ({ signal }) =>
      api.get<DeadlineAssigneeList>(wsPath(wsId, P, 'deadlines', deadlineId, 'assignees'), { signal }),
    enabled,
  });
}

export function useAssignDeadline() {
  const { wsId } = useWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ownerUserId }: { id: string; ownerUserId: string | null }) =>
      api.put<ThisWeekItem>(wsPath(wsId, P, 'deadlines', id, 'owner'), { ownerUserId }),
    onSettled: () => qc.invalidateQueries({ queryKey: notificationKeys.thisWeek(wsId) }),
  });
}
